/**
 * Tagada Rx — case detail page.
 */

'use client';

import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useAccountManager } from '@/hooks/use-account-manager';
import { RxCaseStatusBadge, RxPageShell } from '@/components/rx';
import { formatCents, useRxCase } from '@/lib/api/rx';

export default function RxCaseDetailPage() {
  const { t } = useTranslation('rx');
  const { caseId } = useParams<{ caseId: string }>();
  const { storeRoutes } = useStoreNavigate();
  const { isAccountManager } = useAccountManager();
  const query = useRxCase(caseId);

  if (query.isLoading || !query.data) {
    return (
      <RxPageShell title={t('cases.detailTitle')} description={t('cases.detailLoading')}>
        <Button asChild variant="ghost" size="sm" className="-mt-2 w-fit">
          <Link to={storeRoutes.RX_CASES()}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('cases.backToCases')}
          </Link>
        </Button>
        <Card>
          <CardContent className="p-6">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </CardContent>
        </Card>
      </RxPageShell>
    );
  }

  const { case: rxCase, events, economics } = query.data;

  return (
    <RxPageShell
      title={rxCase.id}
      description={t('cases.detailDescription')}
      actions={<RxCaseStatusBadge status={rxCase.status} />}
    >
      <Button asChild variant="ghost" size="sm" className="-mt-2 w-fit">
        <Link to={storeRoutes.RX_CASES()}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('cases.backToCases')}
        </Link>
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('cases.detail.summary')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
          <div>
            <div className="text-muted-foreground">{t('cases.detail.order')}</div>
            <div className="font-mono text-xs">{rxCase.orderId}</div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('cases.detail.customer')}</div>
            <div className="font-mono text-xs">{rxCase.customerId ?? '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('cases.detail.caseReference')}</div>
            <div className="font-mono text-xs">{rxCase.externalCaseId ?? '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('cases.detail.amount')}</div>
            <div className="font-semibold">
              {formatCents(rxCase.totalAmountMinor, rxCase.currency)}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('cases.detail.submitted')}</div>
            <div>
              {rxCase.submittedAt ? new Date(rxCase.submittedAt).toLocaleString() : '—'}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('cases.detail.lastShipped')}</div>
            <div>
              {rxCase.lastShipmentShippedAt
                ? new Date(rxCase.lastShipmentShippedAt).toLocaleString()
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('cases.detail.nextCharge')}</div>
            <div>
              {rxCase.scheduledNextChargeAt
                ? new Date(rxCase.scheduledNextChargeAt).toLocaleString()
                : '—'}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('cases.detail.terminated')}</div>
            <div>
              {rxCase.terminatedAt ? new Date(rxCase.terminatedAt).toLocaleString() : '—'}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('cases.detail.economics')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div>
            <div className="text-muted-foreground">{t('cases.detail.patientPaid')}</div>
            <div className="font-semibold">
              {formatCents(economics.revenueMinor, economics.currency)}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">
              {economics.consultKind === 'initial'
                ? t('cases.detail.consultationInitial')
                : t('cases.detail.consultationFollowUp')}
            </div>
            <div className="font-semibold">
              {economics.consultFeeMinor != null
                ? `− ${formatCents(economics.consultFeeMinor, economics.currency)}`
                : t('cases.detail.notConfigured')}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('cases.detail.medicationCost')}</div>
            <div className="font-semibold">
              {economics.medicationCostMinor != null
                ? `− ${formatCents(economics.medicationCostMinor, economics.currency)}`
                : t('cases.detail.notConfigured')}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">{t('cases.detail.estimatedNet')}</div>
            <div className="font-bold text-emerald-700">
              {economics.estimatedNetMinor != null
                ? formatCents(economics.estimatedNetMinor, economics.currency)
                : '—'}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('cases.detail.timeline')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {events.length === 0 ? (
            <p className="text-muted-foreground p-6 text-sm">{t('cases.detail.noEvents')}</p>
          ) : (
            <ol className="divide-y">
              {events.map((event) => (
                <li key={event.id} className="flex items-start gap-3 p-4">
                  <Badge variant="outline" className="mt-0.5 text-[10px] uppercase">
                    {event.source === 'mdi' || event.source === 'network'
                      ? t('cases.detail.clinicalSource')
                      : event.source}
                  </Badge>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {event.eventType}
                      <span className="text-muted-foreground font-normal">
                        {new Date(event.postedAt).toLocaleString()}
                      </span>
                    </div>
                    {event.statusAfter ? (
                      <div className="text-muted-foreground text-xs">
                        {t('cases.detail.statusChange')} <span className="font-mono">{event.statusAfter}</span>
                      </div>
                    ) : null}
                    {isAccountManager &&
                    event.rawPayload &&
                    Object.keys(event.rawPayload).length > 0 ? (
                      <pre className="bg-muted mt-2 max-h-32 overflow-auto rounded p-2 text-[10px]">
                        {JSON.stringify(event.rawPayload, null, 2)}
                      </pre>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </RxPageShell>
  );
}
